import React, { Component, createRef } from "react";
import PropTypes from "prop-types";
import styles from "./MobileFixedFooter.module.scss";

/**
 * MobileFixedFooter - A reusable component for fixed-position mobile footers
 * 
 * This component automatically adjusts the page's bottom padding to accommodate
 * its dynamic height, preventing content from being hidden behind it.
 * 
 * Usage:
 *   <MobileFixedFooter className={yourStyles.mobileBtnContainer}>
 *     <button>Your Button</button>
 *     <p>Your content</p>
 *   </MobileFixedFooter>
 */
class MobileFixedFooter extends Component {
  constructor(props) {
    super(props);
    this.containerRef = createRef();
    this.resizeObserver = null;
    this.windowResizeListener = null;
    this.debounceTimeout = null;
    this.lastAppliedHeight = 0;
    this.modalCheckInterval = null;
    this.wasModalOpen = false;
  }

  componentDidMount() {
    this.setupResizeObserver();
    this.setupWindowResizeListener();
    this.setupModalStateChecker();
    // Ensure height is calculated after component is fully mounted and rendered
    this.ensureHeightCalculated();
  }

  ensureHeightCalculated = () => {
    // Retry height calculation multiple times to handle async rendering
    let attempts = 0;
    const maxAttempts = 10;
    
    const tryCalculateHeight = () => {
      if (attempts >= maxAttempts) return;
      
      attempts++;
      const height = this.getActualHeight();
      
      if (height > 0 && this.isMobile()) {
        this.applyPadding(height);
      } else if (height === 0) {
        // Retry after a short delay
        setTimeout(tryCalculateHeight, 100);
      }
    };
    
    // Start after a brief delay to ensure DOM is ready
    setTimeout(tryCalculateHeight, 100);
  };

  componentWillUnmount() {
    this.cleanupResizeObserver();
    this.cleanupWindowResizeListener();
    this.cleanupModalStateChecker();
    // Reset the padding on unmount
    this.resetPadding();
  }

  isMobile = () => {
    return window.innerWidth <= 767;
  };

  findContentContainer = () => {
    // First, try to find .scrollContainer (preferred for scrollable content)
    // This handles cases where MobileFixedFooter is inside a fixed-position component
    // like MenuBar, and we need to find the actual scrollable container in the page
    const scrollContainer = document.querySelector(".scrollContainer");
    if (scrollContainer) return scrollContainer;
    
    // Fallback: Find the closest .mainContainer or .scrollContainer ancestor
    let element = this.containerRef.current;
    while (element && element.parentElement) {
      element = element.parentElement;
      if (
        element.classList &&
        (element.classList.contains("mainContainer") ||
          element.classList.contains("scrollContainer"))
      ) {
        return element;
      }
    }
    // Fallback: find any .mainContainer in the document
    const mainContainer = document.querySelector(".mainContainer");
    if (mainContainer) return mainContainer;
    // Last resort: use body
    return document.body;
  };

  resetPadding = () => {
    const container = this.findContentContainer();
    if (container) {
      container.style.removeProperty("padding-bottom");
    }
  };

  isModalOpen = () => {
    // Check if any ReactModal overlay is currently open
    // ReactModal overlays are typically rendered to document.body with a specific class
    // or have aria-hidden="false" when open
    const modalOverlays = document.querySelectorAll(
      '[class*="ReactModal__Overlay"], [class*="modalOverlay"], [role="dialog"]'
    );
    for (let i = 0; i < modalOverlays.length; i++) {
      const overlay = modalOverlays[i];
      const style = window.getComputedStyle(overlay);
      const ariaHidden = overlay.getAttribute('aria-hidden');
      // Check if overlay is visible (not display: none) and not aria-hidden="true"
      if (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        ariaHidden !== 'true'
      ) {
        return true;
      }
    }
    return false;
  };

  applyPadding = (height) => {
    // Only apply padding on mobile devices
    if (!this.isMobile()) {
      this.resetPadding();
      return;
    }

    // Don't apply padding if a modal is open
    // Modals are overlays and shouldn't affect the page padding
    if (this.isModalOpen()) {
      return;
    }

    // Don't apply padding if height is 0 or invalid
    // This prevents applying only the visual spacing when content isn't rendered yet
    if (!height || height <= 0) {
      return;
    }

    // Default visual spacing between content and footer (in pixels)
    // This creates a nice gap so content doesn't feel cramped against the footer
    const DEFAULT_VISUAL_SPACING = 20;
    const bottomPadding = height + this.props.extraPadding + DEFAULT_VISUAL_SPACING;
    
    // Find the content container dynamically each time to ensure we're targeting the right element
    // This prevents affecting the HorizontalMenu or other fixed elements
    const contentContainer = this.findContentContainer();
    if (contentContainer) {
      // Apply the padding - use !important via setProperty to ensure it overrides any CSS
      contentContainer.style.setProperty('padding-bottom', `${bottomPadding}px`, 'important');
    }
  };

  getActualHeight = () => {
    if (!this.containerRef.current) return 0;
    
    const container = this.containerRef.current;
    
    // Try multiple measurement methods and take the maximum
    // Different contexts (MenuBar vs ConfirmedOffersV2) may require different methods
    const measurements = [];
    
    // Method 1: offsetHeight - includes padding and borders, excludes margins
    // Works well for ConfirmedOffersV2 footerContainer
    const offsetHeight = container.offsetHeight || 0;
    measurements.push(offsetHeight);
    
    // Method 2: scrollHeight - includes all content including overflow
    const scrollHeight = container.scrollHeight || 0;
    measurements.push(scrollHeight);
    
    // Method 3: getBoundingClientRect() - includes padding, borders, transforms
    // Works well for fixed-positioned elements
    const containerRect = container.getBoundingClientRect();
    measurements.push(containerRect.height);
    
    // Method 4: Calculate from children bounding box
    // Useful when container collapses but children are positioned
    const children = container.children;
    if (children.length > 0) {
      const computedStyle = window.getComputedStyle(container);
      const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
      const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;
      const borderTop = parseFloat(computedStyle.borderTopWidth) || 0;
      const borderBottom = parseFloat(computedStyle.borderBottomWidth) || 0;
      
      let minTop = Infinity;
      let maxBottom = -Infinity;
      
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const childRect = child.getBoundingClientRect();
        
        // Calculate relative positions within container
        const childTop = childRect.top - containerRect.top;
        const childBottom = childTop + childRect.height;
        
        minTop = Math.min(minTop, childTop);
        maxBottom = Math.max(maxBottom, childBottom);
      }
      
      if (minTop !== Infinity && maxBottom !== -Infinity) {
        const contentHeight = maxBottom - minTop;
        measurements.push(contentHeight + paddingTop + paddingBottom + borderTop + borderBottom);
      }
      
      // Method 5: Sum all children heights + padding + borders
      let sumHeights = 0;
      for (let i = 0; i < children.length; i++) {
        const child = children[i];
        const childRect = child.getBoundingClientRect();
        sumHeights += childRect.height;
      }
      measurements.push(sumHeights + paddingTop + paddingBottom + borderTop + borderBottom);
    }
    
    // Take the maximum of all measurements to ensure we get the full height
    const totalHeight = Math.max(...measurements.filter(h => h > 0));
    
    return totalHeight || 0;
  };

  setupResizeObserver = () => {
    if (!this.containerRef.current) return;

    const container = this.containerRef.current;

    // Always observe the container itself to catch all size changes
    // This ensures we measure the full container including padding and all children
    const elementToObserve = container;

    // Create ResizeObserver to watch for size changes
    this.resizeObserver = new ResizeObserver((entries) => {
      // Clear any pending debounce
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
      }

      // Debounce the measurement to avoid rapid successive calls
      // Use requestAnimationFrame to ensure DOM is fully laid out before measuring
      this.debounceTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            const isModalOpen = this.isModalOpen();
            
            // If a modal is open, reset padding and don't apply new padding
            // Modals are overlays and shouldn't affect the page padding
            if (isModalOpen) {
              this.resetPadding();
              this.wasModalOpen = true;
              return;
            }

            // If modal was just closed, force recalculation to restore padding
            const modalJustClosed = this.wasModalOpen && !isModalOpen;
            this.wasModalOpen = false;

            // Always use getActualHeight() for consistent measurement
            // Don't rely on entry.contentRect.height as it can be inconsistent
            const height = this.getActualHeight();
            
            // Only apply padding if:
            // 1. We have a valid height
            // 2. The height is different from what we last applied (avoid redundant updates)
            //    OR modal just closed (force recalculation)
            // 3. The height is reasonable (not suspiciously small if we had a larger height before)
            if (height > 0) {
              // If we previously applied a larger height and now get a much smaller one,
              // it's likely a measurement error - ignore it (unless modal just closed)
              if (
                !modalJustClosed &&
                this.lastAppliedHeight > 0 &&
                height < this.lastAppliedHeight * 0.7
              ) {
                return;
              }

              // Apply if height changed OR modal just closed (to restore padding)
              if (height !== this.lastAppliedHeight || modalJustClosed) {
                this.lastAppliedHeight = height;
                this.applyPadding(height);

                // Call optional callback with the new height
                if (this.props.onHeightChange) {
                  this.props.onHeightChange(height);
                }
              }
            }
          });
        });
      }, 50); // 50ms debounce
    });

    // Observe the element (prefer child, fallback to container)
    this.resizeObserver.observe(elementToObserve);

    // Trigger initial height calculation after a brief delay to ensure content is rendered
    // Use requestAnimationFrame to ensure DOM is fully updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (container) {
          const initialHeight = this.getActualHeight();
          if (initialHeight > 0) {
            this.lastAppliedHeight = initialHeight;
            this.applyPadding(initialHeight);
          }
        }
      }, 100);
    });
  };

  setupWindowResizeListener = () => {
    // Listen for window resize to handle screen size changes
    this.windowResizeListener = () => {
      const isModalOpen = this.isModalOpen();
      // If a modal is open, reset padding and don't apply new padding
      if (isModalOpen) {
        this.resetPadding();
        this.wasModalOpen = true;
        return;
      }
      
      // If modal was just closed, update state
      if (this.wasModalOpen && !isModalOpen) {
        this.wasModalOpen = false;
      }
      
      if (this.containerRef.current) {
        const height = this.getActualHeight();
        if (height > 0) {
          this.applyPadding(height);
        }
      }
    };
    window.addEventListener("resize", this.windowResizeListener);
  };

  setupModalStateChecker = () => {
    // Periodically check for modal state changes to restore padding when modal closes
    // This ensures padding is restored even if ResizeObserver doesn't fire immediately
    this.modalCheckInterval = setInterval(() => {
      const isModalOpen = this.isModalOpen();
      
      // If modal was open and is now closed, restore padding
      if (this.wasModalOpen && !isModalOpen) {
        this.wasModalOpen = false;
        // Force recalculation of height and padding
        if (this.containerRef.current) {
          const height = this.getActualHeight();
          if (height > 0) {
            this.lastAppliedHeight = height;
            this.applyPadding(height);
          }
        }
      } else if (isModalOpen) {
        this.wasModalOpen = true;
      }
    }, 200); // Check every 200ms
  };

  cleanupModalStateChecker = () => {
    if (this.modalCheckInterval) {
      clearInterval(this.modalCheckInterval);
      this.modalCheckInterval = null;
    }
  };

  cleanupWindowResizeListener = () => {
    if (this.windowResizeListener) {
      window.removeEventListener("resize", this.windowResizeListener);
      this.windowResizeListener = null;
    }
  };

  cleanupResizeObserver = () => {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    // Clear any pending debounce timeout
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }
  };

  render() {
    const { children, className, style } = this.props;

    return (
      <div
        ref={this.containerRef}
        className={`${styles.mobileFixedFooter} ${className || ""}`}
        style={style}
      >
        {children}
      </div>
    );
  }
}

MobileFixedFooter.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  style: PropTypes.object,
  extraPadding: PropTypes.number,
  onHeightChange: PropTypes.func,
};

MobileFixedFooter.defaultProps = {
  className: "",
  style: {},
  extraPadding: 0,
  onHeightChange: null,
};

export default MobileFixedFooter;

